/* eslint-env es2020 */
// Shared warm-cache for the bottom-nav tabs' initial list fetch. Keyed by
// the exact request URL and deduped via promise reuse: whoever asks for a
// URL first (a real page mount, or a SwipeTabsWrapper prefetch while the
// user is still mid-drag) kicks off the network call, and anyone else
// asking for that same URL within the TTL gets the same in-flight/settled
// promise instead of firing a duplicate request.
//
// Short TTL (2s) ensures it only serves to coalesce in-flight drag/swipe
// prefetches with the landing component's mount fetch, without retaining
// stale data across user edits or live updates.
const TTL = 2000;
const cache = window.__tabPrefetchCache || (window.__tabPrefetchCache = new Map()); // url -> { promise, ts }

export const prefetchUrl = (axiosInstance, url) => {
    if (!url) return null;
    const existing = cache.get(url);
    if (existing && Date.now() - existing.ts < TTL) return existing.promise;

    const promise = axiosInstance.get(url).catch(err => {
        cache.delete(url);
        throw err;
    });
    cache.set(url, { promise, ts: Date.now() });
    return promise;
};

// Clear all warm-cache entries on mutations, tab changes, auth changes, or socket updates.
export const clearPrefetchCache = () => {
    cache.clear();
};

const formatYMD = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

// Best-effort guess at the URL a cold navigation to this tab will request:
// the current month, no active search/filters. If the page actually has
// saved filters (sessionStorage) it'll request something else instead and
// this prefetch just goes unused - harmless, not a correctness dependency.
export const getDefaultTabUrl = (href) => {
    const now = new Date();
    const firstDay = formatYMD(new Date(now.getFullYear(), now.getMonth(), 1));
    const lastDay = formatYMD(new Date(now.getFullYear(), now.getMonth() + 1, 0));

    switch (href) {
        case "/tournaments":
            return `/api/v1.0/tournaments/?start_date=${firstDay}&end_date=${lastDay}&all=true`;
        case "/admin/tournaments":
            return `/api/v1.0/tournaments/?start_date=${firstDay}&end_date=${lastDay}`;
        case "/clubs":
            return `/api/v1.0/clubs/?all=true`;
        case "/admin/clubs":
            return `/api/v1.0/clubs/`;
        case "/players":
            return `/api/v1.0/players/`;
        case "/admin/bases/":
            return `/api/v1.0/players/base/`;
        case "/admin/ratings/":
            return `/api/v1.0/ratings/`;
        default:
            return null;
    }
};

// Same idea as getDefaultTabUrl, one level down: a tournament's own tabs
// (Information/Players/Groups/Sitky/Schedule/Rating), swiped between via
// TournamentSwipeTabs. Best-effort guess at each tab's *first* request.
export const getDefaultTournamentTabUrl = (tabKey, tournament) => {
    if (!tournament?.id) return null;
    const { id, grade } = tournament;

    switch (tabKey) {
        case "players":
            return grade > 1
                ? `/api/v1.0/tournaments/${id}/teams/?ordering=-general-rating`
                : `/api/v1.0/tournaments/${id}/players/?ordering=-rating`;
        case "groups":
            return `/api/v1.0/tournaments/${id}/group-stage/`;
        case "brackets":
            return `/api/v1.0/tournaments/${id}/draw-stage/`;
        case "schedule":
            return `/api/v1.0/tournaments/${id}/schedules/stages/`;
        case "rating":
            return `/api/v1.0/tournaments/${id}/players-rating/?grade=${grade}&ordering=-tournament_score`;
        default:
            return null;
    }
};
